import {
    ButtonGhost,
    ButtonGradientPrimary,
    Colors,
    Icon,
    MultilineInput,
    Text,
    type IconName,
} from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { TranslationKey } from '@/i18n-key';

interface AiPromptQuestionStepProps {
    /** ui-library icon name shown next to the question header. */
    iconName: IconName;
    /** i18n key for the question header. */
    questionKey: TranslationKey;
    /** i18n key for the input placeholder. */
    placeholderKey: TranslationKey;
    value: string;
    onChange: (value: string) => void;
    onNext: () => void;
    onBack?: () => void;
    /** The last question step shows the generate button (with its own label) instead of "Next". */
    isFinal: boolean;
    generateButtonKey?: TranslationKey;
    isGenerating?: boolean;
    rows?: number;
    /** An optional step lets the user move on with a blank answer. */
    optional?: boolean;
    /** 1-based step number, used only for stable data-testids. */
    stepNumber: number;
    dataTestId: string;
}

export const AiPromptQuestionStep = ({
    iconName,
    questionKey,
    placeholderKey,
    value,
    onChange,
    onNext,
    onBack,
    isFinal,
    generateButtonKey,
    isGenerating = false,
    rows = 6,
    optional = false,
    stepNumber,
    dataTestId,
}: AiPromptQuestionStepProps) => {
    const { t } = useTranslation();

    return (
        <div className={'animate-fade-in-up flex flex-col gap-5'}>
            <div className={'mb-3 flex items-center gap-2'}>
                <div className={'!h-10 !w-10'}>
                    <Icon
                        name={iconName}
                        color={Colors.BASE_600}
                        className={'!h-9 !w-9'}
                    />
                </div>
                <Text
                    weight={'semibold'}
                    className={'text-lg text-slate-800 md:text-xl'}
                >
                    {t(questionKey)}
                </Text>
            </div>

            <MultilineInput
                value={value}
                onChange={onChange}
                rows={rows}
                placeholder={t(placeholderKey)}
                dataTestId={`${dataTestId}-input-step${stepNumber}`}
                className={'!border !border-teal-400/30 p-4 md:!p-8'}
            />

            <div
                className={`mt-6 flex items-center gap-3 ${onBack ? 'justify-between' : 'justify-end'}`}
            >
                {onBack && (
                    <ButtonGhost
                        size={'md'}
                        onClick={onBack}
                        leftIcon={
                            <Icon
                                name={'arrow-arrow-left'}
                                size={'sm'}
                                color={Colors.BASE_400}
                            />
                        }
                        dataTestId={`${dataTestId}-back-link`}
                    >
                        {t('common.back')}
                    </ButtonGhost>
                )}

                <ButtonGradientPrimary
                    size={'md'}
                    onClick={onNext}
                    disabled={(!optional && !value.trim()) || isGenerating}
                    dataTestId={
                        isFinal ? `${dataTestId}-generate-button` : `${dataTestId}-next-button`
                    }
                    className={isFinal ? undefined : '!w-fit !min-w-auto !px-7'}
                    leftIcon={
                        isFinal ? (
                            <Icon
                                name={'essential-add-circle'}
                                size={'md'}
                                color={Colors.WHITE}
                            />
                        ) : undefined
                    }
                >
                    {isFinal && generateButtonKey ? t(generateButtonKey) : t('common.next')}
                </ButtonGradientPrimary>
            </div>
        </div>
    );
};
